import { useEffect, useState } from "react";
import {
    Plus, X, Edit3, Trash2, Search,
    ChevronLeft, ChevronRight, Building2,
    Mail, Phone, MapPin, FileText,
} from "lucide-react";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

export default function CompanyPage({ setCurrentPage }) {
    const [companies, setCompanies] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [companyCode, setCompanyCode] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [contactNo, setContactNo] = useState("");
    const [email, setEmail] = useState("");
    const [tin, setTin] = useState("");
    const [logoFile, setLogoFile] = useState(null);
    const [search, setSearch] = useState("");
    const [logoTimestamp, setLogoTimestamp] = useState(Date.now());

    const [page, setPage] = useState(1);
    const perPage = 8;
    const totalPages = Math.ceil(filtered.length / perPage);
    const paginated = filtered.slice((page - 1) * perPage, page * perPage);

    // ──────────────────────────────────────────
    // FETCH
    // ──────────────────────────────────────────
    const fetchCompanies = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/Company`);
            if (!res.ok) throw new Error("Failed to fetch companies");
            const data = await res.json();
            const mapped = data.map((c) => ({
                id: c.id,
                companyCode: c.companyCode,
                companyName: c.companyName,
                address1: c.address1,
                address2: c.address2,
                address3: c.address3,
                contactNo: c.contactNo,
                email: c.email,
                tin: c.tin,
                isActive: c.isActive,
                poPrefix: c.poPrefix,
                hasLogo: c.hasLogo,
            }));
            setCompanies(mapped);
            setFiltered(mapped);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to fetch companies", "error");
        }
    };

    // ──────────────────────────────────────────
    // UPLOAD LOGO
    // ──────────────────────────────────────────
    const uploadLogo = async (companyId, file) => {
        if (!file || !companyId) return false;
        const formData = new FormData();
        formData.append("file", file);
        try {
            const res = await fetch(`${API_BASE_URL}api/Company/${companyId}/logo`, {
                method: "POST",
                body: formData,
            });
            if (!res.ok) throw new Error("Logo upload failed");
            return true;
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Failed to upload logo", "error");
            return false;
        }
    };

    // ──────────────────────────────────────────
    // CREATE
    // ──────────────────────────────────────────
    const createCompany = async () => {
        if (!companyCode || !companyName || !address1) {
            Swal.fire("Oops!", "Please fill in required fields", "warning");
            return;
        }

        const body = {
            CompanyCode: companyCode,
            CompanyName: companyName,
            Address1: address1,
            Address2: address2,
            ContactNo: contactNo,
            Email: email,
            TIN: tin,
        };

        try {
            const res = await fetch(`${API_BASE_URL}api/Company`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) throw new Error("Failed to create company");

            const created = await res.json();
            console.log("✅ Created:", created);

            // ✅ Upload logo after create — use lowercase id
            if (logoFile && created.id) {
                await uploadLogo(created.id, logoFile);
                setLogoTimestamp(Date.now());
            }

            setOpenCreate(false);
            clearForm();
            await fetchCompanies();
            Swal.fire("Success!", "Company created successfully", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };

    // ──────────────────────────────────────────
    // UPDATE
    // ──────────────────────────────────────────
    const updateCompany = async () => {
        if (!companyCode || !companyName || !address1) {
            Swal.fire("Oops!", "Please fill in required fields", "warning");
            return;
        }

        const body = {
            CompanyCode: companyCode,
            CompanyName: companyName,
            Address1: address1,
            Address2: address2,
            ContactNo: contactNo,
            Email: email,
            TIN: tin,
        };

        try {
            // ✅ PUT first
            const res = await fetch(`${API_BASE_URL}api/Company/${selectedCompany.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) throw new Error("Failed to update company");

            // ✅ Then upload logo if new file selected
            if (logoFile && selectedCompany?.id) {
                await uploadLogo(selectedCompany.id, logoFile);
                setLogoTimestamp(Date.now());
            }

            setOpenEdit(false);
            setSelectedCompany(null);
            clearForm();
            await fetchCompanies();
            Swal.fire("Updated!", "Company updated successfully", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };

    // ──────────────────────────────────────────
    // DELETE
    // ──────────────────────────────────────────
    const deleteCompany = async (company) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: `You are about to delete ${company.companyName}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });
        if (result.isConfirmed) {
            try {
                const res = await fetch(`${API_BASE_URL}api/Company/${company.id}`, {
                    method: "DELETE",
                });
                if (!res.ok) throw new Error("Failed to delete company");
                await fetchCompanies();
                Swal.fire("Deleted!", "Company has been deleted.", "success");
            } catch (err) {
                Swal.fire("Error", err.message, "error");
            }
        }
    };

    // ──────────────────────────────────────────
    // EFFECTS
    // ──────────────────────────────────────────
    useEffect(() => { fetchCompanies(); }, []);

    useEffect(() => {
        if (!search.trim()) setFiltered(companies);
        else {
            const term = search.toLowerCase();
            setFiltered(companies.filter(c =>
                c.companyName?.toLowerCase().includes(term) ||
                c.companyCode?.toLowerCase().includes(term)
            ));
        }
        setPage(1);
    }, [search, companies]);

    // ──────────────────────────────────────────
    // HELPERS
    // ──────────────────────────────────────────
    const clearForm = () => {
        setCompanyCode(""); setCompanyName("");
        setAddress1(""); setAddress2("");
        setContactNo(""); setEmail("");
        setTin(""); setLogoFile(null);
    };

    const openEditModal = (c) => {
        setSelectedCompany(c);
        setCompanyCode(c.companyCode ?? "");
        setCompanyName(c.companyName ?? "");
        setAddress1(c.address1 ?? "");
        setAddress2(c.address2 ?? "");
        setContactNo(c.contactNo ?? "");
        setEmail(c.email ?? "");
        setTin(c.tin ?? "");
        setLogoFile(null);
        setOpenEdit(true);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
            <button
                onClick={() => setCurrentPage("referencess")}
                className="mb-2 flex items-center gap-5 text-indigo-600 hover:text-indigo-800 font-semibold transition-colors group ml-8"
            >
                <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                Back to References
            </button>

            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                            <Building2 className="text-indigo-600" size={36} /> Companies
                        </h1>
                        <p className="text-gray-600 mt-2">Manage your company information and settings</p>
                    </div>
                    <button
                        onClick={() => setOpenCreate(true)}
                        className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    >
                        <Plus size={20} /> Create Company
                    </button>
                </div>
            </div>

            <div className="mb-6">
                <div className="relative max-w-md">
                    <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by company name or code..."
                        className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all shadow-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                                <th className="px-6 py-4 text-left text-sm font-semibold">Logo</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Code</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Company Name</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Address</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Contact Info</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">TIN</th>
                                <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {paginated.length > 0 ? paginated.map((c, index) => (
                                <tr key={c.id || `${c.companyCode}-${index}`} className="hover:bg-indigo-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                                            {/* ✅ Use hasLogo flag, add timestamp to bust cache */}
                                            {c.hasLogo ? (
                                                <img
                                                    src={`${API_BASE_URL}api/Company/logo/${c.id}?t=${logoTimestamp}`}
                                                    alt="Logo"
                                                    className="w-full h-full object-contain"
                                                    onError={(e) => {
                                                        e.target.style.display = "none";
                                                        e.target.parentElement.innerHTML = '<div class="text-gray-400 text-xs text-center">No Logo</div>';
                                                    }}
                                                />
                                            ) : (
                                                <Building2 size={24} className="text-gray-400" />
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                            {c.companyCode}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="font-semibold text-gray-800">{c.companyName}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-600 flex items-start gap-2">
                                            <MapPin size={14} className="mt-1 text-gray-400 flex-shrink-0" />
                                            <div>
                                                <div>{c.address1}</div>
                                                {c.address2 && <div className="text-gray-500">{c.address2}</div>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="space-y-1 text-sm">
                                            {c.email && (
                                                <div className="flex items-center gap-2 text-gray-600">
                                                    <Mail size={14} className="text-gray-400" /> {c.email}
                                                </div>
                                            )}
                                            {c.contactNo && (
                                                <div className="flex items-center gap-2 text-gray-600">
                                                    <Phone size={14} className="text-gray-400" /> {c.contactNo}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <FileText size={14} className="text-gray-400" /> {c.tin || "—"}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex justify-center gap-2">
                                            <button onClick={() => openEditModal(c)} className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md" title="Edit">
                                                <Edit3 size={16} />
                                            </button>
                                            <button onClick={() => deleteCompany(c)} className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md" title="Delete">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="7" className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center gap-3 text-gray-400">
                                            <Building2 size={48} />
                                            <p className="text-lg font-medium">No companies found</p>
                                            <p className="text-sm">Try adjusting your search or create a new company</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {paginated.length > 0 && (
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                                Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, filtered.length)} of {filtered.length} companies
                            </span>
                            <div className="flex items-center gap-3">
                                <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm">
                                    <ChevronLeft size={18} />
                                </button>
                                <span className="text-sm font-semibold text-gray-700">Page {page} of {totalPages || 1}</span>
                                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm">
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {(openCreate || openEdit) && (
                <Modal
                    title={openCreate ? "Create New Company" : "Edit Company"}
                    close={() => { setOpenCreate(false); setOpenEdit(false); setSelectedCompany(null); clearForm(); }}
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-4">
                            <Input label="Company Code*" value={companyCode} onChange={setCompanyCode} required />
                            <Input label="Company Name*" value={companyName} onChange={setCompanyName} required />
                            <Input label="Address 1*" value={address1} onChange={setAddress1} required />
                            <Input label="Address 2" value={address2} onChange={setAddress2} />
                        </div>
                        <div className="space-y-4">
                            <Input label="Contact No" value={contactNo} onChange={setContactNo} />
                            <Input label="Email" value={email} onChange={setEmail} type="email" />
                            <Input label="TIN" value={tin} onChange={setTin} />
                        </div>
                        <div
                            className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-indigo-500 hover:bg-indigo-50 transition cursor-pointer group"
                            onClick={() => document.getElementById("logoInput").click()}
                            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) setLogoFile(e.dataTransfer.files[0]); }}
                            onDragOver={(e) => e.preventDefault()}
                        >
                            {logoFile ? (
                                <div className="text-center space-y-3">
                                    <img src={URL.createObjectURL(logoFile)} alt="Preview" className="object-contain h-32 w-32 mx-auto rounded-lg" />
                                    <p className="text-sm text-gray-600">{logoFile.name}</p>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setLogoFile(null); }}
                                        className="text-xs text-red-500 hover:text-red-700"
                                    >Remove</button>
                                </div>
                            ) : (
                                <div className="text-center space-y-3">
                                    {/* ✅ Show existing logo in edit mode */}
                                    {openEdit && selectedCompany?.hasLogo ? (
                                        <>
                                            <img src={`${API_BASE_URL}api/Company/logo/${selectedCompany.id}?t=${logoTimestamp}`} alt="Current Logo" className="object-contain h-24 w-24 mx-auto rounded-lg border" />
                                            <p className="text-xs text-gray-400">Click to replace logo</p>
                                        </>
                                    ) : (
                                        <>
                                            <Building2 size={48} className="mx-auto text-gray-400 group-hover:text-indigo-500 transition" />
                                            <p className="text-gray-500 font-medium">Drag & Drop logo here</p>
                                            <p className="text-sm text-gray-400">or click to browse</p>
                                            <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                                        </>
                                    )}
                                </div>
                            )}
                            <input type="file" accept="image/*" id="logoInput" className="hidden" onChange={(e) => setLogoFile(e.target.files[0])} />
                        </div>
                    </div>

                    <button
                        onClick={openCreate ? createCompany : updateCompany}
                        className="mt-6 w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                    >
                        {openCreate ? "Create Company" : "Update Company"}
                    </button>
                </Modal>
            )}
        </div>
    );
}

function Modal({ title, close, children }) {
    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-5xl relative max-h-[90vh] overflow-y-auto">
                <button onClick={close} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition">
                    <X size={24} />
                </button>
                <h2 className="text-3xl font-bold text-gray-800 mb-8">{title}</h2>
                {children}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", required = false }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
            />
        </div>
    );
}